/**
 * Plugin de runtime do CKEditor que impede apagar os widgets protegidos
 * (variáveis e trechos fixos do template).
 *
 * Toda remoção de conteúdo passa por `model.deleteContent` — Backspace/Delete,
 * Ctrl+X, digitar por cima de uma seleção. Quando a seleção contém widgets
 * protegidos, a remoção é refeita só nos pedaços de texto ENTRE eles: selecionar
 * tudo e apagar limpa o texto e deixa as variáveis e os trechos fixos no lugar.
 * Um widget selecionado sozinho simplesmente não sai.
 *
 * Mesmas restrições dos outros plugins: registrado via `config.extraPlugins` e
 * sem importar nada de `@ckeditor/*`.
 */
export function createProtectedWidgetsPlugin(protectedElements: string[]): any {
  return class ProtectedWidgetsPlugin {
    editor: any;

    /** Evita recursão: as remoções parciais passam de novo pelo mesmo evento. */
    private isDeletingParts = false;

    constructor(editor: any) {
      this.editor = editor;
    }

    init(): void {
      const model = this.editor.model;

      model.on(
        'deleteContent',
        (event: any, [selection, options]: [any, any]) => {
          if (this.isDeletingParts) return;

          const parts = this.deletableParts(selection);

          if (parts === null) return;

          event.stop();

          model.change((writer: any) => {
            this.isDeletingParts = true;

            try {
              // De trás para frente: remover um pedaço não desloca os anteriores.
              [...parts].reverse().forEach((range) => {
                model.deleteContent(model.createSelection(range), options);
              });
            } finally {
              this.isDeletingParts = false;
            }

            // Só widget selecionado (ex.: Backspace logo depois de um chip): o
            // cursor pula para o outro lado dele, e o próximo Backspace já
            // apaga o texto — em vez de ficar preso no chip.
            const range = selection.getFirstRange();
            const target =
              parts.length > 0
                ? parts[0].start
                : options?.direction === 'forward'
                ? range.end
                : range.start;

            // O comando de delete passa a PRÓPRIA cópia da seleção e a aplica
            // no documento depois — ela precisa acompanhar a remoção parcial,
            // senão aponta para posições que não existem mais.
            if (selection === model.document.selection) {
              writer.setSelection(target);
            } else {
              selection.setTo(target);
            }
          });
        },
        { priority: 'high' }
      );
    }

    /**
     * Pedaços da seleção que podem ser apagados, ou `null` quando a seleção não
     * contém nenhum widget protegido (a remoção segue o fluxo normal).
     */
    private deletableParts(selection: any): any[] | null {
      const model = this.editor.model;
      const parts: any[] = [];
      let hasProtected = false;

      for (const range of selection.getRanges()) {
        let start = range.start;

        for (const item of range.getItems()) {
          if (!protectedElements.some((name) => item.is?.('element', name))) {
            continue;
          }

          hasProtected = true;

          const before = model.createPositionBefore(item);
          if (!start.isEqual(before)) parts.push(model.createRange(start, before));

          start = model.createPositionAfter(item);
        }

        if (!start.isEqual(range.end)) {
          parts.push(model.createRange(start, range.end));
        }
      }

      return hasProtected ? parts : null;
    }
  };
}
